import { Drawer, Loader, Pagination } from '@mantine/core';
import {
    IconCircleDot,
    IconExclamationCircle,
    IconPlayerPlay,
    IconPlayerStop,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { BuildInfo, BuildStatus } from '@xver/apis/v1/kufer/builds/model';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { formatDate } from '@xver/utils/date';
import type { LogStreamStatus } from '@xver/hooks/useLogStream';
import useLogStream, { buildKuferBuildLogsUrl } from '@xver/hooks/useLogStream';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBuildListQuery } from '../hooks/useBuildListQuery';

interface BuildsTabPanelProps {
    containerId: string;
}

const BUILD_STATUS_STYLES: Record<BuildStatus, { label: string; color: string; bg: string }> = {
    pending: {
        label: 'Pending',
        color: 'var(--color-status-neutral)',
        bg: 'rgba(115, 115, 115, 0.2)',
    },
    building: {
        label: 'Building',
        color: 'var(--color-status-warning)',
        bg: 'rgba(251, 191, 36, 0.15)',
    },
    pushing: {
        label: 'Pushing',
        color: 'var(--color-status-info)',
        bg: 'rgba(96, 165, 250, 0.15)',
    },
    completed: {
        label: 'Completed',
        color: 'var(--color-status-success)',
        bg: 'rgba(34, 197, 94, 0.15)',
    },
    failed: { label: 'Failed', color: 'var(--color-status-danger)', bg: 'rgba(239, 68, 68, 0.15)' },
};

const LOG_STATUS_STYLES: Record<LogStreamStatus, { label: string; color: string }> = {
    idle: { label: 'Idle', color: 'var(--color-status-neutral)' },
    connecting: { label: 'Connecting', color: 'var(--color-status-warning)' },
    open: { label: 'Streaming', color: 'var(--color-status-success)' },
    closed: { label: 'Closed', color: 'var(--color-status-neutral)' },
    error: { label: 'Error', color: 'var(--color-status-danger)' },
};

function BuildStatusBadge({ status }: { status: BuildStatus }) {
    const style = BUILD_STATUS_STYLES[status] ?? BUILD_STATUS_STYLES.pending;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium"
            style={{ color: style.color, backgroundColor: style.bg }}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: style.color }} />
            {style.label}
        </span>
    );
}

function formatDuration(startIso?: string, endIso?: string): string {
    if (!startIso) return '-';
    const start = new Date(startIso).getTime();
    const end = endIso ? new Date(endIso).getTime() : Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end)) return '-';
    const seconds = Math.max(0, Math.floor((end - start) / 1000));
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

interface BuildDrawerContentProps {
    containerId: string;
    build: BuildInfo;
}

function BuildDrawerContent({ containerId, build }: BuildDrawerContentProps) {
    const { t } = useTranslation('containerEngineInstance');
    const preRef = useRef<HTMLPreElement>(null);
    const [streaming, setStreaming] = useState<boolean>(
        build.status === 'building' || build.status === 'pending' || build.status === 'pushing'
    );

    const isLive = streaming;

    const requestTicket = useCallback(
        () => Endpoints.v1.Kufer.Builds.requestBuildLogTicket(containerId),
        [containerId]
    );

    const buildWsUrl = useCallback(
        (ticket: string) => buildKuferBuildLogsUrl(containerId, ticket),
        [containerId]
    );

    const handleServerClose = useCallback(() => {
        setStreaming(false);
    }, []);

    const {
        logs: streamLogs,
        status: streamStatus,
        error: streamError,
        clear,
    } = useLogStream({
        enabled: isLive,
        requestTicket,
        buildWsUrl,
        maxLines: 5000,
        onServerClose: handleServerClose,
    });

    const {
        data: staticLogsData,
        isPending: isStaticPending,
        error: staticError,
    } = useQuery({
        queryKey: ['apis.v1.kufer.builds.logs', containerId, build.id],
        queryFn: () => Endpoints.v1.Kufer.Builds.getBuildLogs(containerId, build.id),
        enabled: !isLive,
        refetchInterval: false,
    });

    useEffect(() => {
        const el = preRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [streamLogs, staticLogsData]);

    const logStatusStyle = LOG_STATUS_STYLES[streamStatus] ?? LOG_STATUS_STYLES.idle;
    const staticLogsText = staticLogsData?.logs ?? '';
    const liveLogsText = streamLogs.join('\n');
    const showLogs = isLive ? liveLogsText : staticLogsText;
    const errorText = isLive
        ? streamError
        : staticError
          ? ((staticError as { message?: string })?.message ?? 'Failed to load logs.')
          : null;

    return (
        <div className="w-full flex flex-col gap-4">
            <div className="w-full grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <span className="text-fg/68">{t('builds.detailStatus', 'Status')}</span>
                <span>
                    <BuildStatusBadge status={build.status} />
                </span>
                <span className="text-fg/68">{t('builds.detailRepo', 'Repository')}</span>
                <span className="text-fg font-mono text-xs">
                    {build.github_owner}/{build.github_repository}
                </span>
                <span className="text-fg/68">{t('builds.detailBranch', 'Branch')}</span>
                <span className="text-fg font-mono text-xs">{build.github_branch || '-'}</span>
                <span className="text-fg/68">{t('builds.detailCommit', 'Commit')}</span>
                <span className="text-fg font-mono text-xs">
                    {build.github_commit_sha ? build.github_commit_sha.slice(0, 7) : '-'}
                </span>
                <span className="text-fg/68">{t('builds.detailDockerfile', 'Dockerfile')}</span>
                <span className="text-fg font-mono text-xs">{build.dockerfile_path || '-'}</span>
                <span className="text-fg/68">{t('builds.detailImage', 'Image')}</span>
                <span className="text-fg font-mono text-xs">
                    {build.image_name ? `${build.image_name}:${build.image_tag ?? 'latest'}` : '-'}
                </span>
                <span className="text-fg/68">{t('builds.detailStarted', 'Started')}</span>
                <span className="text-fg text-xs">
                    {build.build_started_at
                        ? new Date(build.build_started_at).toLocaleString()
                        : '-'}
                </span>
                <span className="text-fg/68">{t('builds.detailCompleted', 'Completed')}</span>
                <span className="text-fg text-xs">
                    {build.build_completed_at
                        ? new Date(build.build_completed_at).toLocaleString()
                        : '-'}
                </span>
                <span className="text-fg/68">{t('builds.detailDuration', 'Duration')}</span>
                <span className="text-fg text-xs">
                    {formatDuration(build.build_started_at, build.build_completed_at)}
                </span>
                {build.status_message && (
                    <>
                        <span className="text-fg/68">{t('builds.detailMessage', 'Message')}</span>
                        <span className="text-fg/80 text-xs">{build.status_message}</span>
                    </>
                )}
            </div>

            <div className="w-full flex flex-col rounded-md border-[0.5px] border-line overflow-hidden">
                <div className="w-full p-2.5 flex flex-row justify-between items-center bg-surface-1 border-b-[0.5px] border-line">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">
                            {t('builds.logsTitle', 'Build logs')}
                        </span>
                        {isLive && (
                            <div className="flex items-center gap-1.5">
                                <IconCircleDot size={12} style={{ color: logStatusStyle.color }} />
                                <span
                                    className="text-xs font-medium"
                                    style={{ color: logStatusStyle.color }}
                                >
                                    {logStatusStyle.label}
                                </span>
                            </div>
                        )}
                    </div>
                    {isLive ? (
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconPlayerStop size={14} />}
                                onClick={() => setStreaming(false)}
                            >
                                {t('logs.stop', 'Stop')}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={clear}>
                                {t('logs.clear', 'Clear')}
                            </Button>
                        </div>
                    ) : (
                        (build.status === 'building' ||
                            build.status === 'pending' ||
                            build.status === 'pushing') && (
                            <Button
                                size="sm"
                                leftIcon={<IconPlayerPlay size={14} />}
                                onClick={() => setStreaming(true)}
                            >
                                {t('logs.start', 'Start')}
                            </Button>
                        )
                    )}
                </div>
                {errorText && (
                    <div className="w-full px-3 py-2 flex items-center gap-2 bg-red-950/40 light:bg-red-50 border-b-[0.5px] border-line text-red-300 light:text-red-700">
                        <IconExclamationCircle size={14} />
                        <span className="text-xs font-mono">{errorText}</span>
                    </div>
                )}
                {!isLive && isStaticPending ? (
                    <div className="w-full h-60 flex items-center justify-center bg-black">
                        <Loader size="sm" color="white" />
                    </div>
                ) : (
                    <pre
                        ref={preRef}
                        tabIndex={0}
                        className="w-full h-[55vh] min-h-90 m-0 p-3 bg-black text-white/80 font-mono text-xs leading-relaxed overflow-auto whitespace-pre-wrap wrap-break-word"
                    >
                        {showLogs ||
                            t('builds.logsEmpty', 'No log output available for this build.')}
                    </pre>
                )}
            </div>
        </div>
    );
}

export default function BuildsTabPanel({ containerId }: BuildsTabPanelProps) {
    const { t, i18n } = useTranslation('containerEngineInstance');
    const [page, setPage] = useState(1);
    const [selectedBuild, setSelectedBuild] = useState<BuildInfo | null>(null);
    const perPage = 20;

    const { data, isPending } = useBuildListQuery({
        containerId,
        page,
        per_page: perPage,
    });

    const totalPages = useMemo(() => {
        if (!data || !data.total) return 1;
        return Math.max(1, Math.ceil(data.total / (data.per_page || perPage)));
    }, [data]);

    const columns: DataTableColumn<BuildInfo>[] = [
        {
            key: 'status',
            label: t('builds.colStatus', 'Status'),
            render: (b) => <BuildStatusBadge status={b.status} />,
        },
        {
            key: 'commit',
            label: t('builds.colCommit', 'Commit'),
            render: (b) => (
                <span className="font-mono text-xs text-fg/80">
                    {b.github_commit_sha ? b.github_commit_sha.slice(0, 7) : '-'}
                </span>
            ),
        },
        {
            key: 'branch',
            label: t('builds.colBranch', 'Branch'),
            render: (b) => (
                <span className="font-mono text-xs text-fg/68">{b.github_branch || '-'}</span>
            ),
        },
        {
            key: 'imageTag',
            label: t('builds.colImageTag', 'Image tag'),
            render: (b) => (
                <span className="font-mono text-xs text-fg/68">{b.image_tag || '-'}</span>
            ),
        },
        {
            key: 'started',
            label: t('builds.colStarted', 'Started'),
            render: (b) => (
                <span className="text-xs text-fg/68">
                    {b.build_started_at
                        ? formatDate(b.build_started_at, i18n.language, t)
                        : formatDate(b.created_at, i18n.language, t)}
                </span>
            ),
        },
        {
            key: 'duration',
            label: t('builds.colDuration', 'Duration'),
            render: (b) => (
                <span className="text-xs text-fg/68">
                    {formatDuration(b.build_started_at, b.build_completed_at)}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full flex flex-col gap-4">
            <DialogBox
                label={t('builds.title', 'Builds')}
                description={t(
                    'builds.description',
                    'Recent build attempts for this container. Click a row for details.'
                )}
                rightDescription={
                    data ? (
                        <span className="text-xs text-fg/68">
                            {t('builds.total', '{{count}} total', { count: data.total })}
                        </span>
                    ) : null
                }
            >
                <DataTable
                    caption={t('builds.title', 'Builds')}
                    columns={columns}
                    rows={data?.builds}
                    isLoading={isPending}
                    rowKey={(b) => b.id}
                    onRowClick={(b) => setSelectedBuild(b)}
                    emptyMessage={t('builds.empty', 'No builds yet.')}
                />
                {totalPages > 1 && (
                    <div className="w-full p-3 flex justify-end">
                        <Pagination
                            total={totalPages}
                            value={page}
                            onChange={setPage}
                            size="sm"
                            color="gray"
                        />
                    </div>
                )}
            </DialogBox>

            <Drawer
                opened={!!selectedBuild}
                onClose={() => setSelectedBuild(null)}
                position="right"
                size="xl"
                title={
                    selectedBuild ? (
                        <div className="flex flex-col">
                            <span className="text-base font-bold">
                                {t('builds.drawerTitle', 'Build details')}
                            </span>
                            <span className="text-xs text-fg/68 font-mono">{selectedBuild.id}</span>
                        </div>
                    ) : (
                        t('builds.drawerTitle', 'Build details')
                    )
                }
                overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
                transitionProps={{ duration: 150 }}
            >
                {selectedBuild && (
                    <BuildDrawerContent containerId={containerId} build={selectedBuild} />
                )}
            </Drawer>
        </div>
    );
}
